import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  useGetMemberDetailsQuery,
  useUpdateMemberMutation,
  useUpdateMemberEmailMutation,
  useLazyEmailHolderQuery,
  useUpdateUserEmailMutation,
  useDiscardDuplicateAccountMutation,
  useUpdateMemberPhoneMutation,
  useImpersonateUserMutation,
} from "../api/membershipApi";
import { useGetChaptersQuery } from "../../chapter/api/chapterApi";
import { useGetStatesQuery, useGetSectorsQuery, useGetLgasQuery } from "../../settings/api/settingsApi";
import { formatCurrency } from "../../../utils/formatters";
import PageGate from "../../../components/PageGate";
import AccessGate from "../../../components/AccessGate";
import { PERMS } from "../../../utils/perms";
import { useAccess } from "../../../utils/useAccess";
import { useAuth } from "../../../context/AuthContexts";

function fmt(d?: string | null) {
  if (!d) return null;
  return new Date(d).toLocaleDateString("en-NG", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

/**
 * The message an admin should actually see when a save is rejected.
 *
 * Laravel returns 422 with {message, errors: {field: [msg, …]}}. Showing only
 * `message` loses the per-field detail — an admin re-using another member's
 * email was told "Update failed" and had no idea which field or why. Every
 * field message is surfaced instead.
 */
/** Compare stored vs submitted without tripping on case or stray spaces. */
function norm(v: any): string {
  return String(v ?? "").trim().toLowerCase();
}

function validationMessage(e: any): string {
  const errors = e?.data?.errors;

  if (errors && typeof errors === "object") {
    const all = Object.values(errors).flat().filter(Boolean) as string[];
    if (all.length) return all.join(" ");
  }

  return e?.data?.message || "Update failed. Please try again.";
}

/**
 * What the field components below need from the page.
 *
 * They live at module scope rather than inside the page component, and that is
 * load-bearing: a component defined in a render body is a NEW type on every
 * render, so React unmounts and remounts it instead of updating it. Every
 * keystroke in edit mode was destroying the input it was typed into — the caret
 * jumped out, and on a phone the keyboard closed with it. Passing the page's
 * state down as `ctx` costs one prop per call site and makes the inputs stable.
 */
type FieldCtx = {
  editMode: boolean;
  form: Record<string, any>;
  member: any;
  setField: (field: string, value: any) => void;
};

const labelCls = "text-xs font-semibold text-gray-400 mb-1 uppercase tracking-tight";
const controlCls =
  "text-sm border border-blue-200 rounded-lg px-3 py-2.5 md:py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 transition w-full";

function Section({ title, children }: any) {
  return (
    <div className="space-y-4 bg-white p-4 sm:p-6 rounded-2xl border border-gray-200 shadow-sm">
      <h3 className="text-xs font-bold text-blue-600 uppercase tracking-widest border-b border-gray-50 pb-2">
        {title}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {children}
      </div>
    </div>
  );
}

function Info({ label, value, isBold = false }: any) {
  return (
    <div className="flex flex-col min-w-0">
      <span className={labelCls}>{label}</span>
      {/* Emails and addresses are longer than a phone is wide; they wrap
          rather than stretching the card into a sideways scroll. */}
      <span className={`text-sm break-words ${isBold ? "font-bold text-gray-900" : "text-gray-700"}`}>
        {value || <span className="text-gray-300 italic">Not Provided</span>}
      </span>
    </div>
  );
}

/** An input in edit mode, or the plain value in view mode. */
function EditField({
  ctx, label, field, type = "text", allowed, displayValue,
}: {
  ctx: FieldCtx;
  label: string;
  field: string;
  type?: string;
  allowed: boolean;
  displayValue?: any;
}) {
  const show = displayValue !== undefined ? displayValue : ctx.member[field];
  if (!ctx.editMode || !allowed) return <Info label={label} value={show} />;
  return (
    <div className="flex flex-col min-w-0">
      <span className={labelCls}>{label}</span>
      <input
        type={type}
        className={controlCls}
        value={ctx.form[field] ?? ""}
        onChange={(e) => ctx.setField(field, e.target.value)}
      />
    </div>
  );
}

function SelectField({
  ctx, label, field, options, allowed, displayValue, onChangeSideEffect,
}: {
  ctx: FieldCtx;
  label: string;
  field: string;
  options: { id: any; name: string }[];
  allowed: boolean;
  displayValue?: any;
  onChangeSideEffect?: (val: string) => void;
}) {
  const show = displayValue !== undefined ? displayValue : ctx.member[field];
  if (!ctx.editMode || !allowed) return <Info label={label} value={show} />;
  return (
    <div className="flex flex-col min-w-0">
      <span className={labelCls}>{label}</span>
      <select
        className={controlCls}
        value={ctx.form[field] ?? ""}
        onChange={(e) => {
          ctx.setField(field, e.target.value);
          onChangeSideEffect?.(e.target.value);
        }}
      >
        <option value="">— Select —</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
    </div>
  );
}

function ChapterField({ ctx, chapters, allowed }: { ctx: FieldCtx; chapters: any[]; allowed: boolean }) {
  if (!ctx.editMode || !allowed) {
    return <Info label="Chapter" value={ctx.member.chapter?.name} />;
  }
  return (
    <div className="flex flex-col min-w-0">
      <span className={labelCls}>Chapter</span>
      <select
        className={controlCls}
        value={ctx.form.chapter_id ?? ""}
        onChange={(e) => ctx.setField("chapter_id", e.target.value)}
      >
        <option value="">— Select Chapter —</option>
        {chapters.map((c: any) => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
    </div>
  );
}

export default function MemberDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: member, isLoading } = useGetMemberDetailsQuery(id);
  const [updateMember, { isLoading: savingProfile }] = useUpdateMemberMutation();
  const [updateMemberEmail, { isLoading: savingEmail }] = useUpdateMemberEmailMutation();
  const [lookupHolder] = useLazyEmailHolderQuery();
  const [updateUserEmail] = useUpdateUserEmailMutation();
  const [discardAccount] = useDiscardDuplicateAccountMutation();
  // Who is sitting on the address this member's change was refused for. Only
  // ever populated after an actual refusal — see the note where it is set.
  const [clash, setClash] = useState<{ email: string; holder: any } | null>(null);
  const [updateMemberPhone, { isLoading: savingPhone }] = useUpdateMemberPhoneMutation();
  const saving = savingProfile || savingEmail || savingPhone;
  const { data: chapters = [] } = useGetChaptersQuery();
  const { data: states = [] } = useGetStatesQuery();
  const { data: sectors = [] } = useGetSectorsQuery();
  const { can } = useAccess();
  const { beginImpersonation } = useAuth();
  const [impersonateUser] = useImpersonateUserMutation();
  const [loggingInAs, setLoggingInAs] = useState(false);

  const canFullEdit = can.perm(PERMS.member.update);
  const canBasicEdit = can.perm(PERMS.member.updateBasic);
  const canEdit = canFullEdit || canBasicEdit;

  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<Record<string, any>>({});
  const { data: lgas = [] } = useGetLgasQuery(form.state_id, { skip: !form.state_id || !editMode });
  // "warn" = saved, but not everything the admin expected changed — e.g. the
  // member has no login account, so their sign-in address is untouched.
  const [toast, setToast] = useState<{ type: "ok" | "warn" | "err"; msg: string } | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const startEdit = () => {
    setForm({
      title: member?.title || "",
      surname: member?.surname || "",
      firstname: member?.firstname || "",
      othernames: member?.othernames || "",
      dob: member?.dob || "",
      gender: member?.gender || "",
      sector_id: member?.sector?.id || "",
      state_id: member?.state?.id || "",
      lga_id: member?.lga?.id || "",
      chapter_id: member?.chapter?.id || "",
      address: member?.address || "",
      office_address: member?.office_address || "",
      email: member?.email || "",
      phone: member?.phone || "",
      phone2: member?.phone2 || "",
    });
    setEditMode(true);
  };

  const handleSave = async () => {
    // Thunks, not promises: they must run one at a time so a rejected
    // uniqueness check does not land after other writes have already gone out.
    const calls: Array<() => Promise<any>> = [];

    // Profile fields (member table) — always included when editing
    const payload: Record<string, any> = {
      title: form.title,
      surname: form.surname,
      firstname: form.firstname,
      othernames: form.othernames,
      chapter_id: form.chapter_id,
      // Combined name kept in sync on the user record
      name: [form.surname, form.firstname, form.othernames].filter(Boolean).join(" "),
    };

    if (canFullEdit) {
      Object.assign(payload, {
        dob: form.dob,
        gender: form.gender,
        sector_id: form.sector_id,
        state_id: form.state_id,
        local_government_id: form.lga_id,
        address: form.address,
        office_address: form.office_address,
      });

      // Email changed → dedicated endpoint so user account is also updated
      if (form.email !== (member.email || "")) {
        calls.push(() => updateMemberEmail({ id: member.id, email: form.email }).unwrap());
      }

      // Phone changed → dedicated endpoint so user account is also updated
      const phoneChanged =
        form.phone !== (member.phone || "") ||
        form.phone2 !== (member.phone2 || "");
      if (phoneChanged) {
        calls.push(
          () => updateMemberPhone({ id: member.id, phone: form.phone, phone2: form.phone2 }).unwrap()
        );
      }
    }

    // Profile last. Email and phone are the only calls that can fail on
    // uniqueness, so running them first means a rejected edit stops before the
    // rest of the record has been rewritten.
    calls.push(() => updateMember({ id: member.id, body: payload }).unwrap());

    try {
      // Collected so the toast reports what the server actually said, not a
      // blanket "success". The email/phone endpoints warn when a member has no
      // login account — that warning is the difference between "their sign-in
      // address changed" and "it did not".
      const notes: string[] = [];

      // The profile call runs last and returns the member as it now stands, so
      // its payload is the authoritative "after" state.
      let saved: any = null;

      for (const call of calls) {
        const res: any = await call();
        if (res?.user_account_updated === false && res?.message) {
          notes.push(res.message);
        }
        if (res?.data) saved = res.data;
      }

      // Read back what was actually stored before claiming anything. A server
      // that accepted the request but did not apply the value — an older build
      // with no uniqueness check, a column that silently refused the write —
      // must not come back as "updated successfully".
      const notApplied: string[] = [];
      if (saved) {
        if (form.email && norm(saved.email) !== norm(form.email)) {
          notApplied.push(
            `The email address was not changed — "${form.email}" is already in use by another member or account.`,
          );
          // Name the holder. "Already in use" with nobody attached to it is
          // where this used to stop, and there was nothing the secretariat
          // could do next; almost always it is this same person's second
          // account, and seeing that makes the fix obvious.
          try {
            const holder = await lookupHolder(form.email).unwrap();
            if (holder?.found) setClash({ email: form.email, holder });
          } catch {
            // The lookup is an explanation, not the operation. If it fails the
            // refusal above still stands on its own.
          }
        }
        if (form.phone && norm(saved.phone) !== norm(form.phone)) {
          notApplied.push(
            `The phone number was not changed — "${form.phone}" is already in use by another member or account.`,
          );
        }
      }

      if (notApplied.length) {
        setToast({ type: "err", msg: notApplied.join(" ") });
        return; // stay in edit mode so the value can be corrected
      }

      setToast({
        type: notes.length ? "warn" : "ok",
        msg: notes.length
          ? notes.join(" ")
          : "Member details updated successfully.",
      });
      setEditMode(false);
    } catch (e: any) {
      setToast({ type: "err", msg: validationMessage(e) });
    }
  };

  const setField = (field: string, value: any) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="max-w-6xl mx-auto">
          <div className="h-10 bg-gray-200 rounded-xl w-32 mb-6"></div>
          <div className="h-32 bg-white rounded-2xl mb-6"></div>
          <div className="grid grid-cols-1 gap-4 sm:gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-48 bg-white rounded-2xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!member)
    return <div className="text-red-500 font-bold">Member not found</div>;

  const ctx: FieldCtx = { editMode, form, member, setField };

  return (
    <PageGate perm={PERMS.member.detailsAccess}>
      {/* Extra bottom room in edit mode for the phone's fixed save bar. */}
      <div className={`space-y-4 sm:space-y-6 ${editMode ? "pb-28 md:pb-0" : ""}`}>
        {/* Toast — full width on a phone, where a corner pill is half
            off-screen and the message is the point. */}
        {toast && (
          <div
            className={`fixed top-4 left-4 right-4 sm:left-auto sm:top-5 sm:right-5 sm:max-w-md z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-bold transition-all ${
              toast.type === "ok"
                ? "bg-emerald-600 text-white"
                : toast.type === "warn"
                  ? "bg-amber-500 text-white"
                  : "bg-red-600 text-white"
            }`}
          >
            {toast.msg}
          </div>
        )}

        {/* Who is holding the address, and the way out.
            Shown only after a save was actually refused — it explains a wall
            the secretariat kept hitting with nothing to act on. */}
        {clash && (
          <div className="max-w-6xl mx-auto rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-amber-900">
                  “{clash.email}” is already taken
                </p>
                <p className="text-xs text-amber-800 mt-0.5">
                  Free it up by moving it off whatever holds it, then save this member again.
                </p>
              </div>
              <button
                onClick={() => setClash(null)}
                className="text-xs font-bold text-amber-700 hover:underline shrink-0"
              >
                Dismiss
              </button>
            </div>

            {clash.holder?.user && (
              <ClashHolder
                title="Login account"
                subtitle={[
                  clash.holder.user.name,
                  clash.holder.user.membership_no,
                  clash.holder.user.has_member_record ? "has a member record" : "no member record",
                  clash.holder.user.email_verified_at ? "verified" : "unverified",
                ]
                  .filter(Boolean)
                  .join(" · ")}
                blockers={clash.holder.user.discard_blockers ?? []}
                onMove={async (email) => {
                  const res: any = await updateUserEmail({
                    userId: clash.holder.user.id,
                    email,
                  }).unwrap();
                  setToast({ type: "ok", msg: res?.message ?? "Account email updated." });
                  setClash(null);
                }}
                onDiscard={async () => {
                  const res: any = await discardAccount({
                    userId: clash.holder.user.id,
                  }).unwrap();
                  setToast({ type: "ok", msg: res?.message ?? "Duplicate account deleted." });
                  setClash(null);
                }}
              />
            )}

            {clash.holder?.member && (
              <p className="text-xs text-amber-800">
                A member record also holds it —{" "}
                <Link
                  to={`/member-list/${clash.holder.member.id}`}
                  className="font-bold underline"
                >
                  {clash.holder.member.name || clash.holder.member.membership_no || "open it"}
                </Link>
                . Change that record&apos;s email first.
              </p>
            )}

            {Array.isArray(clash.holder?.applications) && clash.holder.applications.length > 0 && (
              <p className="text-xs text-amber-800">
                Held by {clash.holder.applications.length} probationer application(s):{" "}
                {clash.holder.applications
                  .map((a: any) => `${a.name || "applicant"} (${a.status})`)
                  .join(", ")}
                . Moving the account above moves these with it.
              </p>
            )}
          </div>
        )}

        {/* Top Navigation Row. Five buttons do not share a line with a phone,
            so Back keeps its own row and the actions wrap underneath it. */}
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:justify-between md:items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="self-start flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-600 text-sm font-bold rounded-xl hover:bg-gray-50 transition-all shadow-sm active:scale-95"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>

          <div className="flex flex-wrap items-center gap-2 md:gap-3">
            <AccessGate perm={PERMS.billing.access}>
              <Link
                to={`/admin/billing/users/${member?.user?.id}/dues`}
                className="flex-1 md:flex-none text-center px-4 md:px-5 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition-all shadow-md shadow-blue-100 whitespace-nowrap"
              >
                View Billing & Dues
              </Link>
              <Link
                to={`/admin/billing/users/${member?.user?.id}/payments`}
                className="flex-1 md:flex-none text-center px-4 md:px-5 py-2.5 bg-slate-800 text-white text-sm font-bold rounded-xl hover:bg-slate-900 transition-all shadow-md shadow-slate-200 whitespace-nowrap"
              >
                View Payments
              </Link>
            </AccessGate>

            {!editMode && member?.user?.id && (
              <button
                onClick={async () => {
                  const who = [member.title, member.surname, member.firstname].filter(Boolean).join(" ").trim() || member.user?.email || "this user";
                  if (!window.confirm(`Log in as ${who}?\n\nYou will be signed into their account until you click "Exit impersonation".`)) return;
                  setLoggingInAs(true);
                  try {
                    const res = await impersonateUser(member.user.id).unwrap();
                    if (!res?.token || !res?.user) throw new Error("Could not log in as this user.");
                    beginImpersonation(res.token, res.user);
                    window.location.href = "/dashboard"; // full reload → clean session + caches
                  } catch (e: any) {
                    setToast({ type: "err", msg: e?.data?.message || e?.message || "Could not log in as this user." });
                    setLoggingInAs(false);
                  }
                }}
                disabled={loggingInAs}
                className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 md:px-5 py-2.5 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition-all shadow-md shadow-indigo-100 active:scale-95 disabled:opacity-60 whitespace-nowrap"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 12H3m0 0l4-4m-4 4l4 4m6-11h5a2 2 0 012 2v10a2 2 0 01-2 2h-5" />
                </svg>
                {loggingInAs ? "Logging in…" : "Log in as"}
              </button>
            )}

            {canEdit && !editMode && (
              <button
                onClick={startEdit}
                className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 md:px-5 py-2.5 bg-amber-500 text-white text-sm font-bold rounded-xl hover:bg-amber-600 transition-all shadow-md shadow-amber-100 active:scale-95 whitespace-nowrap"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15.232 5.232l3.536 3.536M9 13l6.5-6.5a2 2 0 012.828 2.828L11.828 15.828a4 4 0 01-2.828 1.172H7v-2a4 4 0 011.172-2.828z" />
                </svg>
                Edit Details
              </button>
            )}

            {/* Save/Cancel live at the top of a long form. That is fine on a
                desk, where the form is a few screens; on a phone it is many,
                so the phone gets the fixed bar below instead. */}
            {editMode && (
              <div className="hidden md:flex items-center gap-3">
                <button
                  onClick={() => setEditMode(false)}
                  disabled={saving}
                  className="px-4 py-2.5 bg-white border border-gray-200 text-gray-600 text-sm font-bold rounded-xl hover:bg-gray-50 transition-all active:scale-95"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-5 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition-all shadow-md shadow-blue-100 active:scale-95 disabled:opacity-60"
                >
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Phone save bar — always within thumb reach, however far down the
            form the admin has scrolled. */}
        {editMode && (
          <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 flex gap-3 px-4 py-3 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
            <button
              onClick={() => setEditMode(false)}
              disabled={saving}
              className="flex-1 py-3 bg-white border border-gray-200 text-gray-600 text-sm font-bold rounded-xl active:scale-95"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-[2] py-3 bg-blue-600 text-white text-sm font-bold rounded-xl active:scale-95 disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        )}

        <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6">
          {/* Edit mode banner */}
          {editMode && (
            <div className={`rounded-xl px-4 sm:px-5 py-3 text-xs sm:text-sm font-semibold border ${
              canFullEdit
                ? "bg-amber-50 border-amber-200 text-amber-800"
                : "bg-blue-50 border-blue-200 text-blue-800"
            }`}>
              {canFullEdit
                ? "Full edit mode — you can edit all member details including contact information."
                : "Basic edit mode — you can edit names and chapter only."}
            </div>
          )}

          {/* Header Profile Section */}
          <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center gap-4 sm:gap-5 min-w-0">
              <div className="w-14 h-14 sm:w-20 sm:h-20 shrink-0 bg-blue-600 text-white rounded-2xl flex items-center justify-center text-xl sm:text-3xl font-black shadow-lg shadow-blue-100">
                {(editMode ? form.surname : member.surname)?.[0]}
                {(editMode ? form.firstname : member.firstname)?.[0]}
              </div>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-black text-gray-900 tracking-tight break-words">
                  {editMode ? `${form.title} ${form.surname} ${form.firstname} ${form.othernames}`.trim() : (
                    <>{member.title} {member.surname} {member.firstname}{" "}
                      <span className="font-medium text-gray-500">{member.othernames}</span>
                    </>
                  )}
                </h1>
                <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-2">
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-[10px] font-black rounded-lg uppercase tracking-wider">
                    {member.user?.whois ?? "Member"}
                  </span>
                  <span className="px-3 py-1 bg-gray-100 text-gray-600 text-[10px] font-black rounded-lg uppercase tracking-wider">
                    ID: {member.membership_no}
                  </span>
                  {member.old_member_id && (
                    <span className="px-3 py-1 bg-orange-50 text-orange-600 text-[10px] font-black rounded-lg uppercase tracking-wider">
                      Legacy ID: {member.old_member_id}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:gap-6">
            {/* Personal Details */}
            <Section title="Personal Information">
              <EditField ctx={ctx} label="Title" field="title" allowed={canFullEdit || canBasicEdit} />
              <EditField ctx={ctx} label="Surname" field="surname" allowed={canFullEdit || canBasicEdit} />
              <EditField ctx={ctx} label="First Name" field="firstname" allowed={canFullEdit || canBasicEdit} />
              <EditField ctx={ctx} label="Other Names" field="othernames" allowed={canFullEdit || canBasicEdit} />
              <SelectField ctx={ctx}
                label="Gender"
                field="gender"
                allowed={canFullEdit}
                displayValue={member.gender === "M" ? "Male" : "Female"}
                options={[{ id: "M", name: "Male" }, { id: "F", name: "Female" }]}
              />
              <EditField ctx={ctx} label="Date of Birth" field="dob" type="date" allowed={canFullEdit} displayValue={member.dob} />
              <Info label="Nationality" value={member.nationality} />
              <EditField ctx={ctx} label="Phone" field="phone" type="tel" allowed={canFullEdit} displayValue={member.phone} />
              <EditField ctx={ctx} label="Alternative Phone" field="phone2" type="tel" allowed={canFullEdit} displayValue={member.phone2} />
              <EditField ctx={ctx} label="Email" field="email" type="email" allowed={canFullEdit} displayValue={member.email} />
            </Section>

            {/* Professional Details */}
            <Section title="Professional Standing">
              <Info label="Membership Grade" value={member?.grade?.name || member?.grade} isBold />
              <Info label="Registration Date" value={member.reg_date} />
              <Info label="Current Stage" value={`Stage ${member.stage}`} />
              <SelectField ctx={ctx}
                label="Sector of Practice"
                field="sector_id"
                allowed={canFullEdit}
                displayValue={member.sector?.name || member.sector_of_practice}
                options={(sectors as any[]).map((s: any) => ({ id: s.id, name: s.name }))}
              />
              <Info label="Employment Sector ID" value={member.employment_sector_id} />
              <AccessGate perm={PERMS.member.viewFinancial}>
                <Info label="Outstanding Balance" value={`${formatCurrency(member?.user?.total_debt)}`} isBold />
              </AccessGate>
            </Section>

            {/* Employment Details */}
            <Section title="Employment Details">
              <Info label="Employer Name" value={member.employer_name} />
              <Info label="Employer State ID" value={member.employer_state_id} />
              <Info label="Firm Principal Partner" value={member.firm_principal_partner} />
            </Section>

            {/* Location Details */}
            <Section title="Location & Address">
              <SelectField ctx={ctx}
                label="State"
                field="state_id"
                allowed={canFullEdit}
                displayValue={member.state?.name}
                options={(states as any[]).map((s: any) => ({ id: s.id, name: s.name }))}
                onChangeSideEffect={() => setField("lga_id", "")}
              />
              <SelectField ctx={ctx}
                label="LGA"
                field="lga_id"
                allowed={canFullEdit}
                displayValue={member.lga?.name || member.local_government_id}
                options={(lgas as any[]).map((l: any) => ({ id: l.id, name: l.name }))}
              />
              <ChapterField ctx={ctx} chapters={chapters as any[]} allowed={canFullEdit || canBasicEdit} />
              <div className="md:col-span-2">
                <EditField ctx={ctx} label="Residential Address" field="address" allowed={canFullEdit} displayValue={member.address} />
              </div>
              <div className="md:col-span-1">
                <EditField ctx={ctx} label="Office Address" field="office_address" allowed={canFullEdit} displayValue={member.office_address} />
              </div>
            </Section>

            {/* Progression Timeline */}
            <Section title="Timeline Dates">
              <Info label="Student" value={member.student_date} />
              <Info label="Probationer" value={member.probationer_date} />
              <Info label="Member" value={member.member_date} />
              <Info label="Fellow" value={member.fellow_date} />
              <Info label="Firm" value={member.firm_date} />
              <Info label="Deceased" value={member.deceased_date} />
            </Section>

            {/* Account / User Info */}
            <Section title="Account Information">
              <Info label="Login Email" value={member.user?.email} />
              <Info label="Email Verified" value={member.user?.email_verified_at ? "Yes" : "No"} />
              <Info label="Account Status" value={member.user?.is_up_to_date ? "Up to Date" : "Outstanding"} />
              <Info label="Membership Category" value={member.user?.category?.name} />
              <Info label="Membership Band" value={member.user?.membershipBand?.label} />
              <Info label="Account Created" value={fmt(member.user?.created_at)} />
              <Info label="Last Login" value={fmt(member.user?.last_login_at)} />
            </Section>

            {/* Financial Summary */}
            <AccessGate perm={PERMS.member.viewFinancial}>
              <Section title="Financial Summary">
                <Info label="Advance Balance" value={formatCurrency(member.advance)} isBold />
                <Info label="Total Outstanding" value={formatCurrency(member.user?.total_debt)} isBold />
                <Info label="Financial Status" value={member.user?.is_up_to_date ? "Up to Date" : "Has Outstanding Balance"} />
              </Section>
            </AccessGate>

            {/* Recent Payments */}
            <AccessGate perm={PERMS.member.viewFinancial}>
              {(member.user?.payments ?? []).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6 shadow-sm space-y-4">
                  <h3 className="text-xs font-bold text-blue-600 uppercase tracking-widest border-b border-gray-50 pb-2">
                    Recent Payments
                  </h3>
                  {/* Five columns need more room than a phone has, so the table
                      scrolls in its own box rather than crushing every column. */}
                  <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                    <table className="w-full min-w-[34rem] text-sm">
                      <thead>
                        <tr className="text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">
                          <th className="pb-2">Receipt</th>
                          <th className="pb-2">Amount</th>
                          <th className="pb-2">Method</th>
                          <th className="pb-2">Status</th>
                          <th className="pb-2">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {(member.user?.payments ?? []).map((p: any) => (
                          <tr key={p.id}>
                            <td className="py-2 font-mono text-xs text-gray-500">
                              {p.receipt_number || p.reference?.slice(0, 12) + "…"}
                            </td>
                            <td className="py-2 font-bold">{formatCurrency(p.amount)}</td>
                            <td className="py-2 text-gray-600 capitalize">{p.payment_method || p.gateway}</td>
                            <td className="py-2">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${
                                p.status === "paid" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"
                              }`}>
                                {p.status}
                              </span>
                            </td>
                            <td className="py-2 text-gray-500 text-xs">{fmt(p.paid_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Link
                    to={`/admin/billing/users/${member.user_id}/payments`}
                    className="text-xs text-blue-600 font-bold hover:underline"
                  >
                    View all payments →
                  </Link>
                </div>
              )}
            </AccessGate>

            {/* Logbooks */}
            {(member.user?.logbooks ?? []).length > 0 && (
              <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6 shadow-sm space-y-4">
                <h3 className="text-xs font-bold text-blue-600 uppercase tracking-widest border-b border-gray-50 pb-2">
                  Logbooks
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(member.user?.logbooks ?? []).map((lb: any) => (
                    <div key={lb.id} className="border border-gray-100 rounded-xl p-4 space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-black text-gray-700 uppercase">
                          Stage {lb.stage}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${
                          lb.status === "in_progress"
                            ? "bg-blue-50 text-blue-600"
                            : lb.status === "submitted"
                            ? "bg-amber-50 text-amber-600"
                            : "bg-gray-100 text-gray-500"
                        }`}>
                          {lb.status ?? "pending"}
                        </span>
                      </div>
                      {lb.diet && (
                        <p className="text-xs text-gray-500">{lb.diet.title}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Admin & Internal Audit */}
            <AccessGate perm={PERMS.member.viewAudit}>
              <Section title="Admin & Audit Details">
                <Info label="Mailing Status" value={member.mailsubscribe === 1 ? "Subscribed" : "Unsubscribed"} />
                <Info label="Special Mail" value={member.specialmail} />
                <Info label="Last Profile Update" value={fmt(member.updated_at)} />
                <Info label="Roles" value={(member.user?.roles ?? []).map((r: any) => r.name).join(", ") || "None"} />
              </Section>
            </AccessGate>
          </div>
        </div>
      </div>
    </PageGate>
  );
}

/**
 * One thing holding the contested address, with the way to move it off.
 *
 * A new address is asked for rather than simply clearing the old one: an
 * account with no email cannot sign in and cannot reset itself, so "release
 * it" would quietly lock somebody out of the portal. Almost always the right
 * value is the address the member record was wrong about — the two are being
 * swapped, not deleted.
 */
function ClashHolder({
  title,
  subtitle,
  blockers = [],
  onMove,
  onDiscard,
}: {
  title: string;
  subtitle: string;
  /** Why the account cannot simply be deleted. Empty means it can. */
  blockers?: string[];
  onMove: (email: string) => Promise<void>;
  onDiscard?: () => Promise<void>;
}) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const canDiscard = !!onDiscard && blockers.length === 0;

  const discard = async () => {
    setErr(null);
    // Typed confirmation rather than a window.confirm: this deletes an account
    // and cannot be undone, and an OK button is too easy to press by reflex.
    const typed = window.prompt(
      'This permanently deletes the account, its unpaid application invoice and the files it uploaded. ' +
        'It cannot be undone.\n\nType DELETE to confirm.',
    );
    if (typed !== "DELETE") return;

    setBusy(true);
    try {
      await onDiscard!();
    } catch (e: any) {
      setErr(e?.data?.message || "Could not delete that account.");
    } finally {
      setBusy(false);
    }
  };

  const go = async () => {
    setErr(null);
    const email = value.trim();
    if (!email) return setErr("Enter the address to move this account to.");

    setBusy(true);
    try {
      await onMove(email);
    } catch (e: any) {
      setErr(
        e?.data?.errors?.email?.[0] ||
          e?.data?.message ||
          "Could not move that address.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-amber-200 bg-white p-3 space-y-2">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700">{title}</p>
        <p className="text-sm text-gray-800">{subtitle}</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Move this account to…"
          className="flex-1 border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-100"
        />
        <button
          onClick={go}
          disabled={busy}
          className="px-3 py-2 rounded-lg bg-amber-600 text-white text-xs font-bold hover:bg-amber-700 disabled:opacity-50"
        >
          {busy ? "Moving…" : "Move"}
        </button>
      </div>
      {canDiscard ? (
        <div className="pt-1 border-t border-amber-100">
          <p className="text-[11px] text-gray-500">
            Nothing is attached to this account — no member record, no payment, no logbook.
          </p>
          <button
            onClick={discard}
            disabled={busy}
            className="mt-1 text-xs font-bold text-rose-600 hover:underline disabled:opacity-50"
          >
            Delete this duplicate account instead
          </button>
        </div>
      ) : (
        blockers.length > 0 && (
          <p className="text-[11px] text-gray-500 pt-1 border-t border-amber-100">
            It cannot be deleted: {blockers.join(" ")} Move the address instead.
          </p>
        )
      )}

      {err && <p className="text-[11px] text-rose-600">{err}</p>}
    </div>
  );
}
